import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProducts } from '../context/ProductContext';
import { useImages } from '../context/ImageContext';
import { useCart } from '../context/CartContext';
import firebaseMetadataService from '../services/firebaseMetadataService';
import RobustImage from '../components/RobustImage';
import ProductModal from '../components/ProductModal';
import StorageSelector from '../components/StorageSelector';
import { formatPrice, parsePrice } from '../utils/priceUtils';
import { SearchEmptyState, CategoryEmptyState } from '../components/EmptyState';
import useImagePreloader from '../hooks/useImagePreloader';
import {
  Search,
  Grid,
  List,
  Star,
  ShoppingCart,
  Plus,
  Minus,
  Check,
  RefreshCw,
  Clock
} from 'lucide-react';

const Shop = () => {
  const { category } = useParams();
  const navigate = useNavigate();

  const { products: contextProducts, loading: productsLoading, error: productsError } = useProducts();
  const { images, getImagesByCategory, loading: imagesLoading, error: imagesError, categories: imageCategories } = useImages();
  const { addToCart } = useCart();
  const { preloadCategoryImages, preloadOnHover } = useImagePreloader();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(category || 'All');
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('name');
  const [metadataProducts, setMetadataProducts] = useState([]);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [metadataError, setMetadataError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [newProductsCount, setNewProductsCount] = useState(0);
  const [showNewProductsNotification, setShowNewProductsNotification] = useState(false);

  // Load images with Firebase metadata - Optimized version
  const loadImagesWithMetadata = async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setIsRefreshing(true);
    } else {
      setMetadataLoading(true);
    }

    try {
      console.log('🔄 Loading products with admin metadata from Firebase for Shop...');

      // Get all metadata from Firebase first (more efficient)
      const allMetadata = await firebaseMetadataService.getAllImageMetadata();
      console.log(`📊 Found ${allMetadata.length} products with metadata in Firebase`);

      if (allMetadata && allMetadata.length > 0) {
        const { default: supabaseService } = await import('../services/supabase');
        const imagesWithMetadata = [];

        for (const metadata of allMetadata) {
          // Only include products with complete metadata
          if (metadata && metadata.title && metadata.title.trim()) {
            const imageName = metadata.original_name || metadata.name;
            const publicUrl = supabaseService.getPublicUrl(`products/${imageName}`);

            const productWithMetadata = {
              id: metadata.id || imageName.replace(/\.[^/.]+$/, ""),
              name: imageName,
              publicUrl,
              fullPath: `products/${imageName}`,
              title: metadata.title,
              description: metadata.description || '',
              category: metadata.category || '',
              price: metadata.price || null,
              inStock: metadata.in_stock !== false,
              featured: metadata.featured || false,
              hasMetadata: true,
              source: 'firebase_metadata',
              created_at: metadata.created_at,
              updated_at: metadata.updated_at
            };

            imagesWithMetadata.push(productWithMetadata);
          }
        }

        // Check for new products
        const previousCount = metadataProducts.length;
        const newCount = imagesWithMetadata.length;

        if (previousCount > 0 && newCount > previousCount) {
          const newProductsDetected = newCount - previousCount;
          setNewProductsCount(newProductsDetected);
          setShowNewProductsNotification(true);

          // Hide notification after 5 seconds
          setTimeout(() => {
            setShowNewProductsNotification(false);
          }, 5000);
        }

        setMetadataProducts(imagesWithMetadata);
        setLastRefresh(new Date());
        console.log(`✅ Loaded ${imagesWithMetadata.length} products with admin metadata from Firebase for Shop`);
      } else {
        setMetadataProducts([]);
        console.log('ℹ️ No products with metadata found in Firebase');
      }

      setMetadataError(null);
    } catch (error) {
      console.error('Error loading Firebase metadata products:', error);
      setMetadataError(error.message);
      setMetadataProducts([]);
    } finally {
      setMetadataLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadImagesWithMetadata();

    // Set up automatic refresh every 30 seconds to show new products
    const refreshInterval = setInterval(() => loadImagesWithMetadata(), 30000);

    return () => clearInterval(refreshInterval);
  }, []);

  // Combine loading and error states
  const loading = imagesLoading || productsLoading || metadataLoading;
  const error = imagesError || productsError || metadataError;

  // Get unique categories from metadata products (similar to Gallery)
  const getCategories = () => {
    const uniqueCategories = [...new Set(metadataProducts.map(product => product.category).filter(Boolean))];
    return ['All', ...uniqueCategories];
  };

  const categories = getCategories();

  // Handle category change
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSearchTerm(''); // Clear search when changing category
  };

  // Handle adding image to cart
  const handleAddToCart = (item) => {
    if (item.price) {
      const cartItem = {
        id: item.id || item.name,
        name: item.title || item.name,
        price: item.price,
        image: item.publicUrl || item.image,
        category: item.category,
        description: item.description
      };
      addToCart(cartItem);
    }
  };

  // Filter and sort products (Firebase metadata products only - same as Home page)
  const filteredProducts = useMemo(() => {
    try {
      let items = [];

      // ONLY use Firebase metadata products (admin-configured images)
      if (metadataProducts && Array.isArray(metadataProducts) && metadataProducts.length > 0) {
        items = metadataProducts.filter(product =>
          product && product.id && product.title && product.hasMetadata
        ).map(product => ({
          ...product,
          source: 'firebase_metadata'
        }));
      }

      // Filter by category first
      if (selectedCategory !== 'All') {
        items = items.filter(item => item && item.category === selectedCategory);
      }

      // Filter by search term
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase().trim();
        items = items.filter(item => {
          if (!item) return false;
          const name = item.title || item.name || '';
          const description = item.description || '';
          const category = item.category || '';

          return name.toLowerCase().includes(searchLower) ||
                 description.toLowerCase().includes(searchLower) ||
                 category.toLowerCase().includes(searchLower);
        });
      }

      // Sort items
      items.sort((a, b) => {
        if (!a || !b) return 0;
        switch (sortBy) {
          case 'price-low':
            const priceA = parseFloat(a.price?.toString().replace('K', '')) || 0;
            const priceB = parseFloat(b.price?.toString().replace('K', '')) || 0;
            return priceA - priceB;
          case 'price-high':
            const priceA2 = parseFloat(a.price?.toString().replace('K', '')) || 0;
            const priceB2 = parseFloat(b.price?.toString().replace('K', '')) || 0;
            return priceB2 - priceA2;
          case 'name':
          default:
            const nameA = a.title || a.name || '';
            const nameB = b.title || b.name || '';
            return nameA.localeCompare(nameB);
        }
      });

      return items;
    } catch (error) {
      console.error('Error in filteredProducts:', error);
      return [];
    }
  }, [metadataProducts, images, contextProducts, selectedCategory, searchTerm, sortBy]);

  // Preload images when filtered products change
  useEffect(() => {
    if (filteredProducts && filteredProducts.length > 0) {
      preloadCategoryImages(filteredProducts, selectedCategory === 'All' ? null : selectedCategory);
    }
  }, [filteredProducts, selectedCategory, preloadCategoryImages]);

  // Calculate category counts
  const getCategoryCount = (category) => {
    try {
      const allItems = filteredProducts || [];
      
      if (!Array.isArray(allItems)) {
        return 0;
      }

      if (category === 'All') {
        return allItems.length;
      }

      return allItems.filter(item => 
        item && item.category === category
      ).length;
    } catch (error) {
      console.warn('Error calculating category count:', error);
      return 0;
    }
  };

  const handleViewDetails = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Shop Temporarily Unavailable</h2>
          <p className="text-gray-600 mb-6">We're experiencing technical difficulties. Please try again later.</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  try {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* New Products Notification */}
        {showNewProductsNotification && (
          <div className="fixed top-4 right-4 z-50 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center animate-pulse">
            <Check className="mr-2" size={20} />
            <span>{newProductsCount} new product{newProductsCount !== 1 ? 's' : ''} added!</span>
            <button
              onClick={() => setShowNewProductsNotification(false)}
              className="ml-4 text-green-200 hover:text-white"
            >
              ×
            </button>
          </div>
        )}
        {/* Header Section */}
        <div className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-3xl font-bold text-black">
                    {selectedCategory === 'All' ? 'All Products' : selectedCategory}
                  </h1>
                  {metadataLoading && (
                    <div className="flex items-center text-blue-600">
                      <RefreshCw className="animate-spin mr-1" size={16} />
                      <span className="text-sm">Loading...</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-2">
                  <p className="text-gray-600">
                    {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} found
                  </p>
                  <div className="flex items-center text-xs text-gray-500">
                    <Clock size={12} className="mr-1" />
                    Last updated: {lastRefresh.toLocaleTimeString()}
                  </div>
                </div>
              </div>

              {/* Search and Controls */}
              <div className="mt-6 lg:mt-0 flex flex-col sm:flex-row gap-4">
                {/* Refresh Button */}
                <button
                  onClick={() => loadImagesWithMetadata(true)}
                  disabled={isRefreshing}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Refresh products from Firebase"
                >
                  <RefreshCw className={`mr-2 ${isRefreshing ? 'animate-spin' : ''}`} size={16} />
                  {isRefreshing ? 'Refreshing...' : 'Refresh'}
                </button>
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent w-full sm:w-64"
                  />
                </div>

                {/* Sort */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="name">Sort by Name</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                </select>

                {/* View Mode */}
                <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 ${viewMode === 'grid' ? 'bg-primary-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  >
                    <Grid size={20} />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 ${viewMode === 'list' ? 'bg-primary-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  >
                    <List size={20} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Category Filter Tabs */}
        <div className="bg-gray-50 border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => handleCategoryChange(cat)}
                  onMouseEnter={() => {
                    // Preload images for this category on hover
                    const categoryProducts = filteredProducts.filter(p =>
                      cat === 'All' || p.category === cat
                    );
                    preloadOnHover(categoryProducts.map(p => p.publicUrl || p.image).filter(Boolean));
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === cat
                      ? 'bg-primary-500 text-white shadow-md'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {cat}
                  <span className="ml-2 text-xs opacity-75">
                    ({getCategoryCount(cat)})
                  </span>
                </button>
              ))}
              </div>

              {/* Auto-refresh Status */}
              <div className="flex items-center text-xs text-gray-500 bg-white px-3 py-1 rounded-full border">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                Auto-updating every 30s
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Products Grid/List */}
          <div className="w-full">
              {filteredProducts.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm">
                  {searchTerm.trim() ? (
                    <SearchEmptyState searchTerm={searchTerm} />
                  ) : selectedCategory !== 'All' ? (
                    <CategoryEmptyState category={selectedCategory} />
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-gray-600 text-lg">
                        No products available. Add products through the admin panel.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className={
                  viewMode === 'grid'
                    ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 items-stretch'
                    : 'space-y-4'
                }>
                  {(filteredProducts || []).map((item) => {
                    try {
                      if (!item || (!item.id && !item.name)) {
                        return null;
                      }

                      const key = item.id || item.name || Math.random().toString();
                      
                      return viewMode === 'grid' ? (
                        <ProductCard
                          key={key}
                          product={item}
                          onAddToCart={handleAddToCart}
                          onViewDetails={handleViewDetails}
                        />
                      ) : (
                        <ProductListItem
                          key={key}
                          product={item}
                          onAddToCart={handleAddToCart}
                          onViewDetails={handleViewDetails}
                        />
                      );
                    } catch (renderError) {
                      console.error('Error rendering item:', item, renderError);
                      return (
                        <div key={item?.id || Math.random()} className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <p className="text-red-600 text-sm">Error loading product</p>
                        </div>
                      );
                    }
                  }).filter(Boolean)}
                </div>
              )}
          </div>
        </div>

        {/* Product Modal */}
        <ProductModal
          product={selectedProduct}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />

        {/* Storage Selector */}
        <StorageSelector />
      </div>
    );
  } catch (shopError) {
    console.error('Critical error in Shop component:', shopError);
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-500 text-6xl mb-4">💥</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Something went wrong</h2>
          <p className="text-gray-600 mb-6">The shop page encountered an error. Please refresh to try again.</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }
};

// Product Card Component for Grid View
const ProductCard = ({ product, onAddToCart, onViewDetails }) => {
  const { isInCart, getItemQuantity, updateQuantity } = useCart();
  const [isAdding, setIsAdding] = useState(false);
  const [showQuantity, setShowQuantity] = useState(false);

  const inCart = isInCart(product.id);
  const quantity = getItemQuantity(product.id);

  const handleAddToCart = async () => {
    setIsAdding(true);
    await new Promise(resolve => setTimeout(resolve, 300));
    onAddToCart(product);
    setIsAdding(false);
    setShowQuantity(true);
    setTimeout(() => setShowQuantity(false), 3000);
  };

  const handleQuantityChange = (newQuantity) => {
    updateQuantity(product.id, newQuantity);
    if (newQuantity === 0) {
      setShowQuantity(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm hover:shadow-lg transition-all duration-200 overflow-hidden border border-gray-100 flex flex-col h-full">
      {/* Stock Status Badge */}
      {product.inStock && (
        <div className="bg-green-100 text-green-800 text-xs font-medium px-3 py-1 text-center">
          In Stock
        </div>
      )}

      {/* Image Container */}
      <div className="aspect-square bg-gray-100 relative overflow-hidden p-4">
        <RobustImage
          src={product.publicUrl || product.image}
          alt={product.title || product.name}
          className="w-full h-full object-contain hover:scale-105 transition-transform duration-300"
          fallbackType="electronics"
        />
        {product.featured && (
          <div className="absolute top-2 left-2">
            <span className="bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center">
              <Star className="w-3 h-3 mr-1" />
              Featured
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-3 flex-1 flex flex-col">
        {/* Category */}
        <div className="text-sm text-green-600 font-medium">
          {product.category}
        </div>

        {/* Title */}
        <h3 className="font-bold text-gray-900 text-lg leading-tight">
          {product.title || product.name}
        </h3>

        {/* Description */}
        <p className="text-sm text-gray-600 line-clamp-3 flex-1">
          {product.description}
        </p>

        {/* Last Updated */}
        {product.updated_at && (
          <div className="text-xs text-gray-400 flex items-center">
            <Clock size={10} className="mr-1" />
            Updated: {new Date(product.updated_at.seconds * 1000).toLocaleDateString()}
          </div>
        )}

        {/* Price */}
        {product.price && (
          <div className="text-2xl font-bold text-gray-900">
            {typeof product.price === 'string' && product.price.startsWith('K')
              ? product.price
              : `K${product.price}`
            }
          </div>
        )}

        {/* Actions - Always at bottom */}
        <div className="space-y-2 mt-auto">
          {/* Add to Cart Button */}
          {product.price && product.inStock && !inCart && (
            <button
              onClick={handleAddToCart}
              disabled={isAdding}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center transition-colors duration-200 disabled:opacity-50"
            >
              {isAdding ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Add to Cart
                </>
              )}
            </button>
          )}

          {/* Quantity Controls */}
          {inCart && (showQuantity || quantity > 0) && (
            <div className="flex items-center justify-between bg-gray-50 rounded-lg p-2">
              <span className="text-sm text-green-600 font-medium flex items-center">
                <Check size={16} className="mr-1" />
                In Cart: {quantity}
              </span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleQuantityChange(quantity - 1)}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 p-1 rounded"
                >
                  <Minus size={14} />
                </button>
                <button
                  onClick={() => handleQuantityChange(quantity + 1)}
                  className="bg-primary-500 hover:bg-primary-600 text-white p-1 rounded"
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>
          )}

          {/* Out of Stock Button */}
          {product.price && !product.inStock && (
            <button
              disabled
              className="w-full bg-gray-300 text-gray-500 font-medium py-3 px-4 rounded-lg cursor-not-allowed"
            >
              Out of Stock
            </button>
          )}

          {/* View Details Button */}
          {onViewDetails && (
            <button
              onClick={() => onViewDetails(product)}
              className="w-full text-primary-500 hover:text-primary-600 text-sm font-medium py-2"
            >
              View Details →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Product List Item Component for List View
const ProductListItem = ({ product, onAddToCart, onViewDetails }) => {
  const { isInCart, getItemQuantity, updateQuantity } = useCart();
  const quantity = getItemQuantity(product.id);
  const inCart = isInCart(product.id);

  const handleAddToCart = () => {
    onAddToCart(product);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm hover:shadow-lg transition-all duration-200 p-4 border border-gray-100">
      <div className="flex items-start space-x-4">
        {/* Image */}
        <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 p-2">
          <RobustImage
            src={product.publicUrl || product.image}
            alt={product.title || product.name}
            className="w-full h-full object-contain"
            fallbackType="electronics"
          />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-1 space-y-2">
              {/* Stock Status */}
              {product.inStock && (
                <span className="inline-block bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded">
                  In Stock
                </span>
              )}

              {/* Category */}
              <div className="text-sm text-green-600 font-medium">
                {product.category}
              </div>

              {/* Title */}
              <h3 className="font-bold text-gray-900 text-lg leading-tight">
                {product.title || product.name}
                {product.featured && (
                  <Star className="w-4 h-4 text-yellow-500 inline ml-2" />
                )}
              </h3>

              {/* Description */}
              <p className="text-sm text-gray-600 line-clamp-2">
                {product.description}
              </p>

              {/* Price */}
              {product.price && (
                <div className="text-xl font-bold text-gray-900">
                  {typeof product.price === 'string' && product.price.startsWith('K')
                    ? product.price
                    : `K${product.price}`
                  }
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex-shrink-0 ml-4 space-y-2">
              {product.price && product.inStock && !inCart && (
                <button
                  onClick={handleAddToCart}
                  className="bg-green-600 hover:bg-green-700 text-white font-medium px-6 py-3 rounded-lg flex items-center transition-colors duration-200"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Add to Cart
                </button>
              )}

              {inCart && (
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-green-600 font-medium flex items-center">
                    <Check size={16} className="mr-1" />
                    In Cart: {quantity}
                  </span>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => updateQuantity(product.id, quantity - 1)}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-700 p-1 rounded"
                    >
                      <Minus size={14} />
                    </button>
                    <button
                      onClick={() => updateQuantity(product.id, quantity + 1)}
                      className="bg-primary-500 hover:bg-primary-600 text-white p-1 rounded"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                </div>
              )}

              {product.price && !product.inStock && (
                <button
                  disabled
                  className="bg-gray-300 text-gray-500 font-medium px-6 py-3 rounded-lg cursor-not-allowed"
                >
                  Out of Stock
                </button>
              )}

              {onViewDetails && (
                <button
                  onClick={() => onViewDetails(product)}
                  className="text-primary-500 hover:text-primary-600 text-sm font-medium block"
                >
                  View Details →
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shop;
